<template>
  <div class="invoices-page">
    <el-row>
      <el-col :span="24">
        <el-card class="data">
          <!-- Card header -->
          <div class="data-title flex-space-between" slot="header">
            <p>Invoices Management</p>
            <div class="header-actions">
              <el-input
                v-model="search"
                placeholder="Search invoices"
                clearable
                @keyup.enter.native="applyFilters"
                @clear="applyFilters"
                prefix-icon="el-icon-search"
                class="input-lg mr8"
              />
              <el-date-picker
                v-model="month"
                type="month"
                value-format="yyyy-MM"
                placeholder="All months"
                clearable
                @change="applyFilters"
                class="input-md mr8"
              />
              <el-select
                v-model="paymentMethod"
                placeholder="All payment methods"
                clearable
                @change="applyFilters"
                class="input-md mr8"
              >
                <el-option label="All Payment Methods" value="" />
                <el-option label="PayPal"        value="paypal" />
                <el-option label="Wise"          value="wise" />
<!--                <el-option label="Bank Transfer" value="bank_transfer" />-->
<!--                <el-option label="Manual"        value="manual" />-->
              </el-select>
              <el-select
                v-model="status"
                placeholder="All Statuses"
                clearable
                @change="applyFilters"
                class="input-md mr8"
              >
                <el-option label="All Statuses" value="" />
                <el-option label="Pending"  value="pending" />
                <el-option label="Queried"  value="queried" />
                <el-option label="Resolved" value="resolved" />
                <el-option label="Approved" value="approved" />
                <el-option label="Paid"     value="paid" />
              </el-select>
              <div class="min-amount mr8">
                <span class="mini-label">Min NET</span>
                <el-input-number
                  v-model="amountMin"
                  :min="0"
                  :step="10"
                  controls-position="right"
                  :formatter="formatMoney"
                  :parser="parseMoney"
                  @change="applyFilters"
                  class="amount-min"
                  :precision="0"
                />
              </div>
              <el-tooltip content="Notify selected users" placement="top">
                <el-button
                  type="primary"
                  icon="el-icon-message"
                  :disabled="selected.length === 0 || notifying"
                  :loading="notifying"
                  @click="notifySelected"
                >
                  Notify
                </el-button>
              </el-tooltip>
              <el-tooltip
                v-if="status === 'approved' && paymentMethod === 'paypal'"
                content="Send PayPal payouts for all selected invoices"
                placement="top"
              >
                <button
                  class="brand-btn paypal small"
                  :disabled="selected.length === 0 || payingBatch"
                  @click="payoutSelectedPaypal"
                >
                  <span class="ico" v-html="paypalSvg" />
                  <span>{{ payingBatch ? 'Processing…' : 'PayPal Payout' }}</span>
                </button>
              </el-tooltip>
            </div>
          </div>

          <!-- Tabs -->
          <el-tabs
            type="border-card"
            v-model="activeTab"
            @tab-click="onTabClick"
            class="tabs-block"
          >
            <el-tab-pane label="All" name="all">
              <InvoicesTableCore
                v-if="activeTab === 'all'"
                :rows="rows"
                :loading="loading"
                :page="page"
                :per_page="per_page"
                :total="total"
                @open-detail="openDetail"
                @selection-change="onSelectionChange"
                @page-change="onPageChange"
              />
            </el-tab-pane>

            <el-tab-pane
              :label="unreadQueries > 0 ? `Queried (${unreadQueries})` : 'Queried'"
              name="query"
            >
              <InvoicesTableCore
                v-if="activeTab === 'query'"
                :rows="rows"
                :loading="loading"
                :page="page"
                :per_page="per_page"
                :total="total"
                @open-detail="openDetail"
                @selection-change="onSelectionChange"
                @page-change="onPageChange"
              />
            </el-tab-pane>
          </el-tabs>
        </el-card>
      </el-col>
    </el-row>

    <AdminInvoiceDetailModal
      :visible.sync="detailOpen"
      :invoice-id="detailId || 0"
      @changed="read"
      @seen-updated="fetchUnreadCount"
    />
  </div>
</template>

<script>
import axios from '@/plugins/axios'
import { mapGetters, mapActions, mapMutations, mapState } from 'vuex'
import AdminInvoiceDetailModal from './AdminInvoiceDetailModal.vue'

/** Per-tab default status filter */
const DEFAULT_STATUS_BY_TAB = {
  all:    '',
  query:  'queried',
}

const InvoicesTableCore = {
  name: 'InvoicesTableCore',
  props: {
    rows: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    page: { type: Number, default: 1 },
    per_page: { type: Number, default: 15 },
    total: { type: Number, default: 0 },
  },
  methods: {
    openDetail(row) { this.$emit('open-detail', row) },
    onSelectionChange(rows) { this.$emit('selection-change', rows) },
    onPageChange(p) { this.$emit('page-change', p) },
    money(n, currency) {
      const v = Number(n || 0)
      let prefix = ''
      if (currency === 'USD') prefix = '$'
      else if (currency === 'EUR') prefix = '€'
      else if (currency === 'GBP') prefix = '£'
      return prefix + v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    statusTagType(status) {
      const s = (status || '').toLowerCase()
      if (s === 'approved' || s === 'paid') return 'success'
      if (s === 'notified' ) return 'info'
      if (s === 'queried') return 'danger'
      if (s === 'resolved') return 'warning'
      return 'default'
    },
    prettyStatus(status) {
      if (!status) return ''
      const s = String(status).replace(/_/g, ' ').toLowerCase()
      return s.replace(/\b\w/g, c => c.toUpperCase())
    },
  },
  render(h) {
    const self = this

    const selectionCol = h('el-table-column', { props: { type: 'selection', width: 44 } })

    const invoiceCol = h('el-table-column', { props: { label: 'Invoice #', 'min-width': 220 } }, [
      h('div', { slot: 'default', scopedSlots: undefined })
    ])
    invoiceCol.data = invoiceCol.data || {}
    invoiceCol.data.scopedSlots = {
      default(props) {
        return h('div', { class: 'inv-col' }, [
          h('el-link', {
            props: { type: 'primary' },
            on: { click: () => self.openDetail(props.row) }
          }, [ props.row.invoice_name ]),
          h('div', { class: 'muted tiny' }, `${props.row.period_start} → ${props.row.period_end}`)
        ])
      }
    }

    const userCol = h('el-table-column', { props: { label: 'User', 'min-width': 220 } })
    userCol.data = userCol.data || {}
    userCol.data.scopedSlots = {
      default(props) { return h('span', { class: 'user-name' }, props.row.label) }
    }

    const statusCol = h('el-table-column', { props: { label: 'Status', width: 140, align: 'left' } })
    statusCol.data = statusCol.data || {}
    statusCol.data.scopedSlots = {
      default(props) {
        const type = self.statusTagType(props.row.status)
        return h('span', {
          class: ['status-pill', type]   // instead of el-tag
        }, self.prettyStatus(props.row.status))
      }
    }

    const amountCol = h('el-table-column', { props: { label: 'Amount', width: 140, align: 'right' } })
    amountCol.data = amountCol.data || {}
    amountCol.data.scopedSlots = {
      default(props) { return self.money(props.row.net_total, props.row.currency) }
    }

    const actCol = h('el-table-column', { props: { label: 'Actions', width: 110, fixed: 'right', align: 'right' } })
    actCol.data = actCol.data || {}
    actCol.data.scopedSlots = {
      default(props) {
        return h('el-button', { props: { size: 'mini' }, on: { click: () => self.openDetail(props.row) } }, 'View')
      }
    }

    const userEmailCol = h('el-table-column', {
      props: { label: 'Email', 'min-width': 240 }
    })
    userEmailCol.data = userEmailCol.data || {}
    userEmailCol.data.scopedSlots = {
      default(props) {
        const email =
          (props.row.user && (props.row.user.email || props.row.user.username)) ||
          null

        return h('div', { class: 'email-cell' }, [
          email
            ? h('el-link', {
              props: { type: 'primary', href: `mailto:${email}`, underline: false }
            }, email)
            : h('span', { class: 'muted' }, '—')
        ])
      }
    }

    // Dates columns
    const approvedCol = h('el-table-column', { props: { label: 'Approved At', width: 180, align: 'center' } })
    approvedCol.data = approvedCol.data || {}
    approvedCol.data.scopedSlots = {
      default(props) {
        const v = props.row.approved_at
        return h('span', v ? new Date(v).toLocaleString() : '—')
      }
    }

    const paidCol = h('el-table-column', { props: { label: 'Paid At', width: 180, align: 'center' } })
    paidCol.data = paidCol.data || {}
    paidCol.data.scopedSlots = {
      default(props) {
        const v = props.row.paid_at
        return h('span', v ? new Date(v).toLocaleString() : '—')
      }
    }

// Queried only (will show only when tab=queried)
    const queriedCol = h('el-table-column', { props: { label: 'Queried At', width: 180, align: 'center' } })
    queriedCol.data = queriedCol.data || {}
    queriedCol.data.scopedSlots = {
      default(props) {
        const v = props.row.queried_at
        return h('span', v ? new Date(v).toLocaleString() : '—')
      }
    }

    const columns = [selectionCol, invoiceCol, userCol, userEmailCol, statusCol]

// Add date columns
    if (self.$parent.activeTab === 'query') {
      columns.push(queriedCol)
    } else {
      columns.push(approvedCol, paidCol)
    }

    columns.push(amountCol, actCol)

    const table = h('el-table', {
      ref: 'table',
      props: {
        data: self.rows,
        loading: self.loading,
        'header-cell-class-name': 'table-header'
      },
      on: { 'selection-change': self.onSelectionChange }
    }, columns)

    const pager = h('div', { class: 'table-footer' }, [
      h('el-pagination', {
        props: {
          background: true,
          layout: 'prev, pager, next, total',
          'current-page': self.page,
          'page-size': self.per_page,
          total: self.total
        },
        on: { 'current-change': self.onPageChange }
      })
    ])

    return h('div', [table, pager])
  }
}

export default {
  name: 'AdminInvoicesTabs',
  components: { AdminInvoiceDetailModal, InvoicesTableCore },

  data () {
    return {
      search: '',
      status: 'All Statuses',
      detailOpen: false,
      detailId: 0,
      selected: [],
      notifying: false,
      amountMin: 50,
      paymentMethod: 'All Payment Methods',
      month: '',
      payingBatch: false,
      unreadQueries: 0,
    }
  },
  paypalSvg: '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M19 4.2c-1.1-.9-2.8-1.2-5-1.2H9.7c-.5 0-.9.3-1 .8L6.1 18.8c-.1.5.3 1 .8 1H10l.5-3.2c.1-.5.5-.8 1-.8h1.6c3.3 0 5.9-1.3 6.7-5.1.3-1.6.1-2.9-.8-3.9z" fill="#fff" opacity=".6"/><path d="M17.7 6.1c-.8-.8-2.2-1.1-4.1-1.1H10c-.5 0-.9.3-1 .8L7.3 18.4c-.1.5.3 1 .8 1h1.9l.4-2.6c.1-.5.5-.8 1-.8h1.6c2.4 0 4.3-.5 5.4-1.7 1.3-1.3 1.7-3.4 1.2-5.2-.3-1-1-1.9-1.9-2.6z" fill="#fff"/></svg>',
  computed: {
    ...mapState('invoices', ['activeTab', 'loading']),
    ...mapGetters('invoices', ['rows', 'page', 'per_page', 'total']),
  },

  created () {
    this.status = DEFAULT_STATUS_BY_TAB[this.activeTab] || ''
    this.amountMin = this.activeTab === 'query' ? 0 : 0
    this.read({
      search: this.search,
      status: this.status,
      min_amount: this.amountMin,
      payment_method: this.paymentMethod,
      month: this.month
    })
    this.fetchUnreadCount()
  },

  methods: {
    ...mapActions('invoices', ['read', 'notify', 'switchTab', 'goToPage']),
    ...mapMutations('invoices', ['setActiveTab']),

    onTabClick (tabPane) {
      const tab = tabPane.name
      this.switchTab(tab)
      this.status = DEFAULT_STATUS_BY_TAB[tab] || ''
      this.amountMin = tab === 'query' ? 0 : 50
      if (tab === 'query') this.fetchUnreadCount()

      this.read({
        search: this.search,
        status: this.status,
        min_amount: this.amountMin,
        payment_method: this.paymentMethod,
        month: this.month,
      })
    },

    async fetchUnreadCount() {
      try {
        const { data } = await axios.admin.get('/invoices/queries/unread-count')
        this.unreadQueries = data.count || 0
      } catch (err) {
        console.error('Failed to fetch unread count', err)
      }
    },

    applyFilters () {
      this.read({
        search: this.search,
        status: this.status,
        min_amount: this.amountMin,
        payment_method: this.paymentMethod,
        month: this.month,
      })
    },

    openDetail (row) {
      this.detailId = Number(row.id)
      this.detailOpen = true
    },

    onSelectionChange (rows) {
      this.selected = rows || []
    },

    async notifySelected () {
      const ids = this.selected.map(r => r.id)
      if (!ids.length) return
      try {
        await this.$confirm(
          `Send notification email to ${ids.length} selected invoice(s)?`,
          'Confirm',
          { type: 'warning', confirmButtonText: 'Yes', cancelButtonText: 'No' }
        )
      } catch (e) { return }

      this.notifying = true
      try {
        await this.notify({ invoice_ids: ids })
        this.$message.success('Notifications sent.')
        this.selected = []
      } finally {
        this.notifying = false
      }
    },

    async payoutSelectedPaypal () {
      console.log('payoutSelectedPaypal clicked with', this.selected);
      const ids = this.selected.map(r => r.id)
      if (!ids.length) return

      try {
        await this.$confirm(
          `Start PayPal payout for ${ids.length} approved invoice(s)?`,
          'Confirm',
          { type: 'warning', confirmButtonText: 'Yes', cancelButtonText: 'No' }
        )
      } catch (e) {
        return
      }

      this.payingBatch = true
      try {
        await axios.admin.post('/invoices/payout-paypal/batch', { invoice_ids: ids })

        this.$message.success('PayPal payouts started.')
        this.selected = []
        this.applyFilters()
      } finally {
        this.payingBatch = false
      }
    },

    formatMoney (val) {
      const n = Number(val || 0)
      return '$' + n.toLocaleString(undefined, { maximumFractionDigits: 0 })
    },
    parseMoney (val) {
      const cleaned = String(val || '').replace(/[^\d.]/g, '')
      return cleaned ? Number(cleaned) : 0
    },
    setMinAmount (v) {
      this.amountMin = Number(v) || 0
      this.applyFilters()
    },

    onPageChange (p) { this.goToPage(p) },
  },
}
</script>
<style scoped>
.data-title { display: flex; align-items: center; justify-content: space-between; }
.data-title p { margin: 0; font-size: 18px; font-weight: 700; }
.header-actions { display: flex; align-items: center; gap: 8px; }
.mr8 { margin-right: 8px; }

.input-lg :deep(.el-input__inner),
.input-lg { width: 250px}
.input-md { width: 180px}
.status-select :deep(.el-input__inner) { height: 36px; }

.tabs-block :deep(.el-tabs__header) { margin: 0 !important; }
.tabs-block :deep(.el-tabs__nav-wrap::after) { height: 1px; }

.mini-label {
  display:inline-block;
  font-size:12px;
  color:#6b7280;
  margin-right:6px;
}
.min-amount { display:flex; align-items:center; gap:8px; }
.quick-chips { display:flex; align-items:center; gap:6px; }
.quick-chips .chip { cursor:pointer; }
.chip-reset { font-size:12px; margin-left:4px; }
.inv-col .muted { color:#6b7280; }
.brand-btn {
  position: relative;
  display:inline-flex; align-items:center; gap:10px;
  border: none;
  padding: 12px 16px;
  border-radius: 12px;
  font-weight:800;
  color: #fff;
  cursor: pointer;
  transition: transform .05s ease, filter .2s ease, opacity .2s ease;
  box-shadow: 0 6px 18px rgba(0,0,0,.12);
}
.brand-btn:active { transform: translateY(1px); }
.brand-btn:disabled { opacity:.45; cursor: not-allowed; filter: grayscale(.3); }

/* Compact header variant */
.brand-btn.small {
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1;
}

/* PayPal look */
.brand-btn.paypal {
  background: linear-gradient(135deg, #003087, #0070ba);
}
.brand-btn.paypal .ico { display:inline-flex; }
.brand-btn.paypal .ico svg { filter: drop-shadow(0 1px 0 rgba(0,0,0,.15)); }
/* put these in your existing <style scoped> block */
.invoices-page :deep(.status-pill) {
  font-weight: 700;
  font-size: 13px;
  letter-spacing: .3px;
  padding: 4px 10px;
  border-radius: 20px;
  border: none;
  color: #fff;
}

.invoices-page :deep(.status-pill.success) {
  background: linear-gradient(135deg, #16a34a, #22c55e);
}
.invoices-page :deep(.status-pill.info) {
  background: linear-gradient(135deg, #0ea5e9, #38bdf8);
}
.invoices-page :deep(.status-pill.warning) {
  background: linear-gradient(135deg, #f59e0b, #fbbf24);
}
.invoices-page :deep(.status-pill.danger) {
  background: linear-gradient(135deg, #dc2626, #ef4444);
}
.invoices-page :deep(.status-pill.default) {
  background: #9ca3af;
}
.invoices-page :deep(td) {
  font-size: 13px;
}
.invoices-page :deep(.el-table .cell) {
  white-space: nowrap;
}
</style>